import { IBillingMethods, ICheckoutState } from '../../interfaces';
import { validateZipCode } from '../Components/Shared/ZipCodeInput/ZipCodeInput';
import { isValidEmail } from './isValidEmail';
import { validatePassword } from './isValidPassword';

const validateBasicShippingFields = (form: ICheckoutState) => {
  const { shipCountry, shipState, shipZip, shipZipError } = form;

  const hasCountry = Boolean(shipCountry?.trim());
  const hasState = shipCountry === 'GB' || Boolean(shipState?.trim());
  const hasZip = Boolean(shipZip?.trim());
  const isZipValid = hasZip && validateZipCode(shipZip, shipCountry);
  const noZipError = !shipZipError;

  return hasCountry && hasState && hasZip && isZipValid && noZipError;
};

const isPhoneNumberValid = (phoneNumber: string): boolean => {
  // Remove all non-numeric characters
  const sanitizedPhoneNumber = phoneNumber.replace(/\D/g, '');

  // UK numbers with country code (44) should be 12 digits
  if (sanitizedPhoneNumber.slice(0, 2) === '44') {
    return sanitizedPhoneNumber.length === 12;
  }

  // US/CA numbers should be 10 digits (not including country code)
  // If the number starts with 1, it's likely a country code, so we check for 11 digits
  if (sanitizedPhoneNumber.startsWith('1') && sanitizedPhoneNumber.length > 1) {
    return sanitizedPhoneNumber.length === 11;
  }

  // Otherwise, expect 10 digits for US/CA numbers
  return sanitizedPhoneNumber.length === 10;
};

// Additional validation for the full shipping form fields
const validateFullShippingFields = (form: ICheckoutState) => {
  const { shipFirstName, shipLastName, shipAddress1, shipCity, phoneNumber } = form;

  const hasFirstName = Boolean(shipFirstName?.trim());
  const hasLastName = Boolean(shipLastName?.trim());
  const hasAddress = Boolean(shipAddress1?.trim());
  const hasCity = Boolean(shipCity?.trim());
  const hasPhone = Boolean(phoneNumber?.trim());
  const isPhoneValid = hasPhone && isPhoneNumberValid(phoneNumber);

  return hasFirstName && hasLastName && hasAddress && hasCity && hasPhone && isPhoneValid;
};

const validateForm = (
  form: ICheckoutState,
  _billingMethod: IBillingMethods,
  isCollapsibleVariation: boolean = false,
) => {
  const { email, password } = form;

  const basicValidation =
    isValidEmail(email?.trim()) &&
    validatePassword(password?.trim()) &&
    validateBasicShippingFields(form);

  if (isCollapsibleVariation) {
    const fullValidation = basicValidation && validateFullShippingFields(form);
    return fullValidation;
  }

  return basicValidation;
};

export { validateForm };
